import { useRef, useEffect } from 'react';
import { useThemeObserver } from '@atlaskit/tokens';
import { checkThirdParty } from '@atlassian/jira-browser-storage-controls';
import { PRODUCT_DISCOVERY_PROJECT } from '@atlassian/jira-common-constants';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import normaliseUrl from '@atlassian/jira-platform-url-normalisation';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { APP_KEY_NAME_MAPPING } from './constants';
import { COMMOM_AREA, type IPendoWidgetProps } from './types';

let pendingRequestJira = Promise.resolve();

function reportError(error: Error, id: string, message: string) {
	// As the operation like initialize/update/teardown are asynchronous, there's a chance that window.pendo
	// could be deleted while switching contexts between JPD and Jira hence the window.pendo might not be available and we would report these issues
	fireErrorAnalytics({
		meta: {
			id,
			packageName: 'jiraCommonPendoAnalytics',
		},
		attributes: {
			message,
		},
		error,
	});
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const initializePendoWithChecking = async (apiKey: string, appName: string, pendoConfig: any) => {
	const id = `pendo-agent-${apiKey}`;
	try {
		if (window.pendo && window.pendo.teardown && apiKey !== window.pendo.apiKey) {
			window.pendo.teardown();
			delete window.pendo;
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		reportError(error, 'jiraPendoAnalyticsTeardown', 'Failed to teardown pendo');
	}

	await checkThirdParty('pendo-main', async () => {
		if (
			!ff('pendo-jira-load-only-for-eng-users_q9w6l') ||
			pendoConfig?.visitor?.language_preference.startsWith('en')
		) {
			let script: HTMLScriptElement | HTMLElement | null = document.getElementById(id);
			if (script) {
				// @ts-expect-error as id is not in the global window
				window.pendo = window[id];
				try {
					if (window?.globalPendoAppName === appName) {
						const updateInterval = setInterval(() => {
							if (
								window.pendo !== undefined &&
								typeof window.pendo.initialize === 'function' &&
								typeof window.pendo.updateOptions === 'function'
							) {
								if (apiKey !== window?.pendo?.apiKey) {
									window.pendo.initialize(pendoConfig);
								} else {
									window.pendo.updateOptions(pendoConfig);
								}
								clearInterval(updateInterval);
							}
						}, 1000);
					}
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					reportError(
						error,
						'jiraPendoAnalyticsInitializeUpdate',
						'Failed to initialize/update pendo',
					);
				}
			} else {
				// eslint-disable-next-line jira/js/no-document-script
				script = document.createElement('script');
				script.id = id;
				if (script instanceof HTMLScriptElement) {
					script.defer = true;
				}
				script.onload = () => {
					// @ts-expect-error as id is not in the global window
					window[id] = window.pendo;
					try {
						if (window?.globalPendoAppName === appName) {
							const initializeInterval = setInterval(() => {
								if (window.pendo !== undefined && typeof window.pendo.initialize === 'function') {
									window.pendo.initialize(pendoConfig);
									clearInterval(initializeInterval);
								}
							}, 1000);
						}
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
					} catch (error: any) {
						reportError(error, 'jiraPendoAnalyticsInitialize', 'Failed to initialize pendo');
					}
				};
				if (script instanceof HTMLScriptElement) {
					script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
				}
				document.head.appendChild(script);
			}
		}
	});
};

const addPendoWidget = (props: IPendoWidgetProps) => {
	const {
		atlassianAccountId,
		cloudId,
		cloudName,
		appEditions,
		apiKey,
		editionKey,
		appName,
		language,
		displayMode,
	} = props;
	// Added Location Config
	const pendoConfig = {
		visitor: {
			id: atlassianAccountId,
			language_preference: language,
			display_mode: displayMode,
		},
		account: {
			id: `${appName}-${cloudId}`,
			workspace_name: cloudName,
			edition: editionKey === 'common' ? 'na' : appEditions?.[editionKey],
		},
		location: {
			transforms: [
				{
					attr: 'href',
					action: 'Replace',
					data: (href: string) => `${normaliseUrl(href)}`,
				},
				{
					attr: 'pathname',
					action: 'Replace',
					data: (pathname: string) => `${pathname}/${window?.globalPendoAppName}`,
				},
			],
		},
	};

	pendingRequestJira = pendingRequestJira.then(() =>
		initializePendoWithChecking(apiKey, appName, pendoConfig),
	);
};

export const PendoAnalytics = () => {
	const tenantContext = useTenantContext();
	const { colorMode } = useThemeObserver();
	const loadingRef = useRef(false);
	const { loading, error, data } = useProjectContext();
	useEffect(() => {
		// If the project context is loading, we don't want to render the Pendo widget
		// we only want to show pendo when we are inside a project
		if (loading) {
			return;
		}

		// If the project context is loaded, let's make sure that there was no
		// errors and the data exists.
		const projectContextLoaded = !loading && !error && data;

		const isProjectLoaded = loadingRef.current || projectContextLoaded;
		loadingRef.current = loading;

		// Pendo Snippet is only for JWM JSM JSW products and NOT for JPD as they have separate implementation
		if (isProjectLoaded && data?.projectType === PRODUCT_DISCOVERY_PROJECT) {
			return;
		}

		// as checked above, there should always be projectType, but we'll provide a
		// sensible default just in case
		const jiraAppName = data?.projectType || COMMOM_AREA;
		const { apiKey, appName, editionKey } = APP_KEY_NAME_MAPPING[jiraAppName];
		window.globalPendoAppName = appName;

		// by this point, we know that we are in a project and we have the correct
		// data to render the pendo widget
		const pendoProps: IPendoWidgetProps = {
			atlassianAccountId: tenantContext.atlassianAccountId,
			cloudId: tenantContext.cloudId,
			cloudName: tenantContext.cloudName,
			appEditions: tenantContext?.appEditions,
			// By default, the colorThemeObserver will return undefined - that means it is light
			displayMode: colorMode || 'light',
			language: tenantContext.locale,
			appName, // Fix: Cast appName to AppName type
			apiKey,
			editionKey, // Assign a default value of type EditionKey
		};

		addPendoWidget(pendoProps);

		return () => {
			// In non-project area guides should not be displayed. setting it empty means location API will not display any guides.
			window.globalPendoAppName = '';
		};
	}, [
		loading,
		error,
		data,
		tenantContext,
		tenantContext.atlassianAccountId,
		tenantContext.cloudId,
		tenantContext.cloudName,
		tenantContext.locale,
		colorMode,
		loadingRef,
	]);

	return null;
};
